import { WindowTitleProvider } from "./WindowTitleProvider";
import { StringUtil } from "../StringUtil";
import { LogManager } from "../logging/LogManager";

const log = LogManager.getLogger("core.WindowTitle");

export class WindowTitle {
    private static titleStack: (() => string)[] = [];

    private static get provider(): WindowTitleProvider {
        return WindowTitleProvider.get();
    }

    public static get(): string {
        const value = this.titleStack.length > 0 ? this.titleStack[this.titleStack.length - 1]() : null;
        return StringUtil.isEmptyString(value) === false ? value : this.provider.defaultTitle;
    }

    /**
     * Push a new title onto the stack and refreshes the document title.
     * @param value - The title to set. Can be a string or a function that returns a string.
     * @param breadcrumb - Used for logging purposes to track the source of the title change.
     */
    public static push(value?: string | (() => string), breadcrumb?: string): void {
        if (typeof value === "function") {
            this.titleStack.push(value);
            this.refresh(`${breadcrumb} > Pushed (function)`);
        } else {
            this.titleStack.push(() => value);
            this.refresh(`${breadcrumb} > Pushed (string)`);
        }
    }

    /**
     * Pops the last title from the stack and refreshes the document title.
     * @param breadcrumb - Used for logging purposes to track the source of the pop action.
     */
    public static pop(breadcrumb?: string): void {
        if (this.titleStack.length > 0)
            this.titleStack.pop();
        this.refresh(`${breadcrumb} > Popped`);
    }

    /**
     * Resets the title stack and pushes a new title.
     * @param value - The title to set. Can be a string or a function that returns a string.
     * @param breadcrumb - Used for logging purposes to track the source of the reset action.
     */
    public static resetWith(value?: string | (() => string), breadcrumb?: string) {
        this.titleStack = [];
        this.push(value, `${breadcrumb} > Reset Stack`);
    }

    /**
     * Refreshes the document title based on the current title stack.
     * @param breadcrumb - Used for logging purposes to track the source of the refresh action.
     */
    public static refresh(breadcrumb?: string): void {
        document.title = this.provider.finalizeTitle(this.get());
        this.logTitleStack(`${breadcrumb} > Refreshed`);
    }

    private static logTitleStack(breadcrumb: string): void {
        if (!log.isDebugEnabled())
            return;

        const titleList = [];
        this.titleStack.map((title) => {
            if (typeof title === "function")
                titleList.push(title());
            else
                titleList.push(title);
        });
        log.debug(`Refresh trail: [${breadcrumb}]`, titleList);
    }
}
